import { CORE_ANNOTATION_PREDICATES } from '@/lib/coreLanguage';
import type {
  CodeMeaning,
  ColumnRole,
  ColumnSpec,
  Dependency,
  ImportSpec,
  KeyResolution,
  NewClass,
  Normalize,
} from '@/lib/api';
import type { OntologySchema, SchemaClass, SchemaProperty } from '@/lib/useOntology';

/**
 * The column-mapping MODEL: the per-column editable state the wizard grid
 * renders, its round-trip to the engine `ImportSpec`, and the declared-term
 * look-up used before any new term is minted. Pure data + functions — the
 * grid itself lives in `components/ColumnMappingStep.tsx`.
 *
 * EVERY CELL IS DESCRIBED AT TWO LEVELS (ruling 4.8, 2026-09-26): what it is,
 * then how it relates to the columns identified as IDs. A row is not one
 * record — a row of a match sheet names a game, a player and a team — so no
 * column is "the key" and nothing lands on "this row's record". Each ID column
 * is its own entity; every other column says which ID column it describes.
 */

/** Level 1: what the column holds. */
export type ColumnJob =
  /** Each value is one thing — a player, a game. Standing alone, or linked
   *  from another ID column's thing (a player's team). */
  | 'id'
  /** A value about an ID column's thing: a number, a date, text, its name. */
  | 'attribute'
  /** The kind of thing another ID column's value is, per row. */
  | 'kind'
  /** A value that stands for something other than itself. */
  | 'code'
  /** A saved mapping's row-qualifier column. Carried, never authored here. */
  | 'tag'
  | 'skip';

/** The datatype of an attribute's literal. */
export type ValueTypeChoice = 'text' | 'number' | 'integer' | 'date' | 'time' | 'datetime' | 'boolean';

/** A key column's optional second job: the datatype of the fact it ALSO
 *  records. `''` means the key only identifies. */
export type KeyValueChoice = '' | ValueTypeChoice;

/** How an ID column's identity is made: the cell IS the ID (reformatted where
 *  needed), the cell is a NAME and an ID is generated, several columns are
 *  combined, or a surrogate is built from the whole row. */
export type MintStrategy = 'from_column' | 'generated' | 'composite' | 'surrogate';

/** Whether an ID column creates new things or matches existing ones by name. */
export type KeyMatch = 'mint' | 'match_or_create' | 'match_only';

/** What a code column's values mean: they name a term of a list, or they
 *  assert something outright. */
export type CodeReads = 'term' | 'means';

/** One row of a code's rule: this value asserts this statement. */
export interface CodeMeaningRow {
  value: string;
  predicate: string;
  object: string;
  objectIsIri: boolean;
}

/** Annotation predicates offered by name on the attribute panel, so a business
 *  user reaches `:label` by asking for the record's name. Each is part of the
 *  QROS core language, so choosing one creates nothing. */
export const ANNOTATION_CHOICES: { predicate: string; label: string }[] = [
  { predicate: ':label', label: "the record's name" },
  { predicate: ':comment', label: 'a description' },
  { predicate: ':definition', label: 'a definition' },
  { predicate: ':scopeNote', label: 'a scope note' },
  { predicate: ':note', label: 'a note' },
  { predicate: ':example', label: 'an example' },
  { predicate: ':altLabel', label: 'another name for the record' },
];

/** A relationship from a Class column's thing to the thing another Class
 *  column names on the same row, stored on the column it starts from. */
export interface MappingLink {
  predicate: string;
  to: string;
  /** The Class column whose thing the relationship holds in (`@context`);
   *  `''` for none. */
  context: string;
}

/** A relationship being written in step 2's add row, before Add is pressed. */
export interface RelationshipDraft {
  from: string;
  predicate: string;
  to: string;
  context: string;
}

export const EMPTY_DRAFT: RelationshipDraft = { from: '', predicate: '', to: '', context: '' };

/** The add row as it stands against the mapping: a column that is no longer
 *  a Class column, or that repeats the subject or the object, is not chosen.
 *  What the row shows and what it holds are then the same thing. */
export function currentDraft(d: RelationshipDraft, mappings: ColumnMapping[]): RelationshipDraft {
  const things = mappings.filter(isThingColumn).map((m) => m.column);
  const from = things.includes(d.from) ? d.from : '';
  const to = things.includes(d.to) && d.to !== from ? d.to : '';
  const context = things.includes(d.context) && d.context !== from && d.context !== to ? d.context : '';
  return { from, predicate: d.predicate, to, context };
}

/** Whether anything has been entered in the add row. */
export function draftStarted(d: RelationshipDraft): boolean {
  return Boolean(d.from || d.to || d.context || d.predicate.trim());
}

/** A part of the add row that Add cannot do without. */
export type DraftPart = 'from' | 'predicate' | 'to';

/** What the add row still needs before it can be added, in the order the row
 *  is read. Empty when it is ready. */
export function draftNeeds(d: RelationshipDraft): DraftPart[] {
  return [
    ...(d.from ? [] : (['from'] as const)),
    ...(d.predicate.trim() ? [] : (['predicate'] as const)),
    ...(d.to ? [] : (['to'] as const)),
  ];
}

const DRAFT_PART_WORDS: Record<DraftPart, string> = {
  from: 'the column it links from (domain)',
  predicate: 'a name for the link',
  to: 'the column it links to (range)',
};

/** The missing parts as one phrase: "the subject, a name for the property and the object". */
export function needsInWords(needs: DraftPart[]): string {
  const words = needs.map((n) => DRAFT_PART_WORDS[n]);
  const last = words.pop() ?? '';
  return words.length ? `${words.join(', ')} and ${last}` : last;
}

/** One column's editable state — job + the fields that job needs. */
export interface ColumnMapping {
  column: string;
  job: ColumnJob;

  /** Attribute, a linked ID, and a code that names a term: what the statement
   *  is called. Blank mints one from the header. */
  predicate: string;
  /** Attribute: the datatype of the literal. */
  valueType: ValueTypeChoice;
  /** ID: the kind of thing its values are. Code-as-term: the list's class. */
  klass: string;
  /** When `klass` was CREATED here, the class it was created under and the
   *  name it was given; empty for a class the ontology already declares.
   *  Carried to the spec so the import declares it (61.34). */
  klassParent: string;
  klassLabel: string;
  /** Level 2: the ID column this column relates to.
   *
   *  For an attribute, a kind or a code: whose it is. `''` is allowed only
   *  when the sheet has exactly one ID column, and means that one.
   *
   *  For an ID: the ID column whose thing links TO this one (`Player
   *  :playsForTeam Team` on the Team column names Player). `''` means the ID
   *  stands on its own. */
  onColumn: string;
  /** The ID column whose thing the statement holds in, recorded as
   *  `@context`: a player's minutes hold in the game on the same row, so two
   *  games' minutes are two facts, not a conflict (61.31). `''` for none.
   *  Offered on a value, a code, and an ID linked from another. */
  context: string;
  /** A class picked as the subject of a value or relationship instead of a
   *  column, when no single ID column holds it: kept so the column is flagged
   *  until the user picks the column. `''` once it resolves. */
  subjectClass: string;
  /** A Class column's relationships to the things other ID columns name on
   *  the same row, declared on the column they start from: on Player,
   *  `:competesIn` to the game column (UAT 2026-09-27). */
  links: MappingLink[];
  /** The header names a relationship (ruled 2026-09-30, 4.9): the cells are
   *  things, and each stands in that object property with another column's
   *  thing. The statement is on this column: `onColumn` is its subject,
   *  `predicate` its name, `context` where it holds. Completed in step 2. */
  relationship: boolean;
  /** A date-and-time value whose date is in another column (Kickoff_Time
   *  with its date in Match_Date). `''` for none. */
  dateFrom: string;

  /** Code-as-term: a code the list lacks becomes a term with its meaning
   *  pending, rather than being dropped. */
  createIfMissing: boolean;

  /** Code: whether its values name a term or assert something outright. */
  codeReads: CodeReads;
  /** Code, `means`: the rule, one row per value that says something. */
  codeMeanings: CodeMeaningRow[];

  /** ID: whether its values create new things or MATCH ones already in the
   *  store by name. */
  keyMatch: KeyMatch;
  /** ID standing alone: the kind of value it ALSO records as a fact, or `''`
   *  for none. A jersey number identifies AND is a fact you sort on (61.22).
   *  Lowers to a SECOND spec column on the same source column. */
  keyValueType: KeyValueChoice;
  /** ID: how its identity is made. */
  idStrategy: MintStrategy;
  /** Composite mint: the other columns appended to make the ID unique. */
  idAlso: string[];
  /** ID: the column doubles as the thing's name. Carried through the
   *  round-trip — hardcoding false here silently dropped every `:label` the
   *  first preview showed (audit W3). */
  isLabel: boolean;
  /** Report-shaped sheets: a blank cell means the same as the one above.
   *  Any column (61.36). */
  carryDown: boolean;

  /** Missing-value sentinels: cells equal to one of these are treated as blank
   *  so the statement is dropped. */
  blankTokens: string[];
  /** Value/key transforms: replace pairs applied while building the value.
   *  Non-replace normalize ops (date parsing, blank_if, to_boolean) are carried
   *  through untouched. */
  replacePairs: { from: string; to: string }[];
  /** Entity: a regular-expression edit of the cell before it becomes a UUID
   *  (`\.` to `-` for a dotted identifier). Both empty for none. */
  regexPattern: string;
  regexReplacement: string;
  /** The spec role EXACTLY as it arrived, for roles the grid cannot author.
   *
   *  Today that is the tag column. The grid used to surface one as Skip, and
   *  `mappingsToColumns` rebuilds every role from the grid on any edit — so a
   *  saved template's tag columns were written back as `skip` on the first
   *  keystroke in any other column, silently, and the only visible trace was a
   *  changed statement count. Carrying the role verbatim and handing it back
   *  untouched means a job the grid cannot express can only be replaced on
   *  purpose. */
  preservedRole?: ColumnRole | undefined;
  /** The person has not said what this column is. A suggestion is shown
   *  beside it and applies nothing until accepted; an unanswered column reads
   *  nothing and holds the mapping at step 1 (product owner, 2026-09-28). */
  unanswered?: boolean;
  /** What the server suggests for an unanswered column, from its header and
   *  the ontology alone. */
  suggestion?: ColumnMapping | undefined;
}

/** Columns fresh from a suggestion: each unanswered, its suggestion beside it. */
export function suggestedMappings(suggested: ColumnMapping[]): ColumnMapping[] {
  return suggested.map((s) => ({ ...emptyMapping(s.column), unanswered: true, suggestion: s }));
}

/** The column once the person has answered it, whatever the answer. */
export function answered(m: ColumnMapping): ColumnMapping {
  return { ...m, unanswered: false, suggestion: undefined };
}

/** A column's suggestion as it stands once accepted. A suggestion accepted
 *  as an Entity gets the ObjectProperty row every Entity column carries to
 *  page 2, named from its header, unless the suggestions already relate the
 *  column: as the domain of a link, or as the range of another column's. A
 *  suggestion that relates every column then opens page 2 with nothing held. */
export function acceptSuggestion(mappings: ColumnMapping[], s: ColumnMapping): ColumnMapping {
  const related = mappings.some((x) => {
    const t = x.suggestion ?? x;
    return (
      (t.column === s.column && (t.links.length > 0 || (t.relationship && t.onColumn.trim()))) ||
      t.links.some((l) => l.to === s.column) ||
      (t.relationship && t.onColumn.trim() === s.column)
    );
  });
  return s.job === 'id' && !s.relationship && !related
    ? answered({ ...s, relationship: true, predicate: s.predicate.trim() || mintedPredicate(s.column) })
    : answered(s);
}

/** Every unanswered column that has a suggestion, accepted. */
export function acceptSuggestions(mappings: ColumnMapping[]): ColumnMapping[] {
  return mappings.map((m) => (m.unanswered && m.suggestion ? acceptSuggestion(mappings, m.suggestion) : m));
}

/** The Entity column a value column's cells describe, as the sheet's own
 *  analysis reads it: the one other Entity column, else the Entity column
 *  whose values determine this column's, by the profile's dependencies. Null
 *  when the sheet cannot say; the row then asks in words. Shown as a
 *  suggestion with Accept, applied by nothing else. */
export function suggestedDomain(mappings: ColumnMapping[], column: string, dependencies: Dependency[]): string | null {
  const entities = idColumns(mappings).filter((c) => c !== column);
  if (entities.length === 1) return entities[0] ?? null;
  const best = dependencies
    .filter((d) => d.dependent === column && entities.includes(d.determinant))
    .sort((a, b) => b.confidence - a.confidence)[0];
  return best?.determinant ?? null;
}

/** Whether two specs are one mapping: the same answers for every column, the
 *  same classes created and the same identifiers, read through the one form
 *  the pages edit, so a spec saved by the server and the one the pages lower
 *  compare alike. */
export function sameMapping(a: ImportSpec, b: ImportSpec): boolean {
  const key = (s: ImportSpec) =>
    JSON.stringify({
      namespace: s.namespace,
      columns: specToMappings(s),
      classes: s.new_classes ?? [],
      identifiers: s.identifiers ?? [],
    });
  return key(a) === key(b);
}

/** A blank column mapping — every field at the value that does nothing. */
export function emptyMapping(column: string): ColumnMapping {
  return {
    column,
    job: 'skip',
    predicate: '',
    valueType: 'text',
    klass: '',
    klassParent: '',
    klassLabel: '',
    onColumn: '',
    context: '',
    subjectClass: '',
    links: [],
    relationship: false,
    dateFrom: '',
    createIfMissing: true,
    codeReads: 'term',
    codeMeanings: [],
    keyMatch: 'mint',
    keyValueType: '',
    idStrategy: 'from_column',
    idAlso: [],
    isLabel: false,
    carryDown: false,
    blankTokens: [],
    replacePairs: [],
    regexPattern: '',
    regexReplacement: '',
  };
}

/** Spreadsheet column letter for an index: 0 -> A, 25 -> Z, 26 -> AA. */
export function columnLetter(idx: number): string {
  let n = idx;
  let s = '';
  do {
    s = String.fromCharCode(65 + (n % 26)) + s;
    n = Math.floor(n / 26) - 1;
  } while (n >= 0);
  return s;
}

function keyMatchToResolution(k: KeyMatch): KeyResolution {
  return k === 'mint'
    ? { mode: 'mint' }
    : { mode: 'match_by_label', create_if_missing: k === 'match_or_create' };
}

function resolutionToKeyMatch(r: KeyResolution | undefined): KeyMatch {
  if (!r || r.mode === 'mint' || r.mode === 'match_by_id') return 'mint';
  return r.create_if_missing ? 'match_or_create' : 'match_only';
}

/** The ID columns another column can relate to: every `id` column, and a code
 *  that names a term (it resolves to a thing too). */
export function idColumns(mappings: ColumnMapping[]): string[] {
  return mappings
    .filter((m) => m.job === 'id' || (m.job === 'code' && m.codeReads === 'term'))
    .map((m) => m.column);
}

/** The ID columns that stand on their own — the only ones whose kind a `kind`
 *  column can give (a linked ID's kind is its class). */
export function standaloneIdColumns(mappings: ColumnMapping[]): string[] {
  return mappings.filter((m) => m.job === 'id' && !m.onColumn).map((m) => m.column);
}

/** One reason the mapping cannot run yet, on the column it concerns. */
export interface MappingProblem {
  column: string | null;
  /** The control the user changes: the subject, or the context. */
  field?: 'header' | 'class' | 'subject' | 'property' | 'context';
  text: string;
}

/** Everything that would stop the mapping lowering, in plain words, each on
 *  the column the user must change. Empty means it runs. */
export function mappingProblems(mappings: ColumnMapping[]): MappingProblem[] {
  const problems: MappingProblem[] = [];
  const open = mappings.filter((m) => m.unanswered);
  if (open.length > 0) {
    problems.push({
      column: null,
      text: `${open.length} column${open.length === 1 ? ' is' : 's are'} not answered yet: accept the suggestion or say what each one is.`,
    });
    for (const m of open) {
      problems.push({ column: m.column, field: 'header', text: 'Not answered yet.' });
    }
  }
  const ids = idColumns(mappings);
  const standalone = standaloneIdColumns(mappings);
  if (ids.length === 0) {
    problems.push({
      column: null,
      text: 'No column is a thing yet. Mark each column whose cells are things in a list, a player, a game, a venue, as “A thing in a list” (Entity), with its class.',
    });
    return problems;
  }
  const soleStandalone = standalone.length === 1 ? standalone[0] : null;
  for (const m of mappings) {
    if (isThingColumn(m)) {
      // Every thing is a thing of some class: without one its statements say
      // nothing about what it is, and no relationship has a domain or range.
      if (!m.klass.trim()) {
        problems.push({
          column: m.column,
          field: 'class',
          text: `Choose the class of the things in ${m.column}, or create one.`,
        });
      }
      if (carriesPropertyRow(m, mappings)) {
        const from = m.onColumn.trim();
        if (!from) {
          problems.push({
            column: m.column,
            field: 'subject',
            text: `Choose the thing that links to ${m.column} (the domain column), or remove the row.`,
          });
        } else if (from === m.column || !ids.includes(from)) {
          problems.push({
            column: m.column,
            field: 'subject',
            text: `${from} is not a thing column, so it cannot be the domain.`,
          });
        }
        if (!m.predicate.trim()) {
          problems.push({ column: m.column, field: 'property', text: `Name the link to ${m.column}.` });
        }
        const rc = m.context.trim();
        if (rc && (rc === m.column || !ids.includes(rc))) {
          problems.push({ column: m.column, field: 'context', text: `${rc} is not a thing column, so it cannot be the context.` });
        }
      }
      for (const l of m.links) {
        if (!l.predicate.trim()) {
          problems.push({ column: m.column, field: 'property', text: `Name the link from ${m.column} to ${l.to}.` });
        }
        if (l.to === m.column || !ids.includes(l.to)) {
          problems.push({
            column: m.column,
            field: 'property',
            text: `${l.to || 'The range'} is not a thing column, so ${l.predicate || 'the property'} cannot have it as range.`,
          });
        }
        const lc = l.context.trim();
        if (lc && (lc === m.column || !ids.includes(lc))) {
          problems.push({
            column: m.column,
            field: 'context',
            text: `${lc} is not a thing column, so it cannot be the context.`,
          });
        }
      }
      continue;
    }
    if (m.job !== 'attribute' && m.job !== 'kind' && m.job !== 'code') continue;
    const on = m.onColumn.trim();
    const ctx = m.context.trim();
    if (ctx && (ctx === m.column || !ids.includes(ctx))) {
      problems.push({
        column: m.column,
        field: 'context',
        text: `${ctx} is not a thing column, so it cannot be the context.`,
      });
    }
    if (!on && m.subjectClass.trim()) {
      const holders = columnsOfClass(mappings, m.subjectClass).filter((c) => c !== m.column);
      problems.push({
        column: m.column,
        field: 'subject',
        text:
          holders.length === 0
            ? `No thing column holds ${m.subjectClass}. Choose the thing it is about (the domain column) instead.`
            : `${holders.join(' and ')} all hold ${m.subjectClass}. Choose which one it is about (the domain).`,
      });
      continue;
    }
    if (!on) {
      if (!soleStandalone) {
        problems.push({ column: m.column, field: 'subject', text: `Choose the thing ${m.column} is about (its domain column).` });
      }
      continue;
    }
    const allowed = m.job === 'kind' ? standalone : ids;
    if (!allowed.includes(on) || on === m.column) {
      problems.push({
        column: m.column,
        field: 'subject',
        text:
          m.job === 'kind'
            ? `${on} is not a thing column standing alone, so its class cannot come from this column.`
            : `${on} is not a thing column, so it cannot be the domain.`,
      });
    }
  }
  return problems;
}

/** The ID columns whose things are of the class, for a subject picked by
 *  class instead of by column. */
export function columnsOfClass(mappings: ColumnMapping[], klass: string): string[] {
  const k = klass.trim();
  return mappings.filter((m) => m.job === 'id' && m.klass.trim() === k).map((m) => m.column);
}

/** What the column header means: the kinds of thing a header can name
 *  (ruled 2026-09-30, 4.9). A class of things; an attribute of one; a
 *  relationship, whose cells are things standing in that object property
 *  with another column's thing; an annotation (label, comment, description)
 *  of one; or nothing. */
export type HeaderChoice = 'class' | 'attribute' | 'relationship' | 'label' | 'comment' | 'description' | 'skip' | 'carried';

/** The predicate each annotation header writes. */
export const ANNOTATION_PREDICATES: Record<'label' | 'comment' | 'description', string> = {
  label: ':label',
  comment: ':comment',
  description: ':definition',
};

function annotationOf(predicate: string): 'label' | 'comment' | 'description' | null {
  const p = predicate.trim();
  for (const [k, v] of Object.entries(ANNOTATION_PREDICATES)) if (v === p) return k as 'label' | 'comment' | 'description';
  return null;
}

/** What each cell holds, in the second radio group. */
export type CellChoice = 'label' | 'uuid' | 'value' | 'qualifier' | 'class';

/** Whether a column's cells are things: an ID column, or a code column whose
 *  codes name terms of a list. Only these are subjects and objects of
 *  relationships. */
export function isThingColumn(m: ColumnMapping): boolean {
  return m.job === 'id' || (m.job === 'code' && m.codeReads === 'term');
}

export function headerChoice(m: ColumnMapping): HeaderChoice {
  switch (m.job) {
    case 'skip':
      return 'skip';
    case 'tag':
      return 'carried';
    case 'id':
      return m.relationship ? 'relationship' : 'class';
    case 'code':
      return m.codeReads === 'term' ? 'class' : 'attribute';
    case 'attribute':
      return annotationOf(m.predicate) ?? 'attribute';
    case 'kind':
      return 'attribute';
  }
}

export function cellChoice(m: ColumnMapping): CellChoice | null {
  switch (m.job) {
    case 'id':
      if (m.keyValueType) return 'value';
      return m.isLabel || m.idStrategy === 'generated' ? 'label' : 'uuid';
    case 'attribute':
      return m.predicate.trim() === ':label' ? 'label' : 'value';
    case 'kind':
      return 'class';
    case 'code':
      return 'qualifier';
    default:
      return null;
  }
}

/** Columns of names that hold one class make their IDs one way, so the same
 *  name is one thing whichever column it is in: the Team in Team, Home_Team
 *  and Away_Team. The column just changed takes the way its class's other
 *  name columns use when it joins them, and sets it for all of them when the
 *  person changes it. */
export function alignNameColumns(before: ColumnMapping[], next: ColumnMapping[], idx: number): ColumnMapping[] {
  const named = (m: ColumnMapping | undefined) =>
    m !== undefined && m.job === 'id' && !m.unanswered && Boolean(m.klass.trim()) && cellChoice(m) === 'label';
  const changed = next[idx];
  if (!changed || !named(changed)) return next;
  const klass = changed.klass.trim();
  const peers = next.filter((m, i) => i !== idx && named(m) && m.klass.trim() === klass);
  const first = peers[0];
  if (!first) return next;
  const was = before[idx];
  const toggled = named(was) && was?.klass.trim() === klass && was.idStrategy !== changed.idStrategy;
  const strategy = toggled ? changed.idStrategy : first.idStrategy;
  return next.map((m, i) =>
    (i === idx || (named(m) && m.klass.trim() === klass)) && m.idStrategy !== strategy
      ? { ...m, idStrategy: strategy }
      : m,
  );
}

/** The relationships a mapping's spec holds, in the ontology's terms: what
 *  the import is sent, not what the screen remembers. */
export function specRelationships(spec: ImportSpec): string[] {
  const classOf = (column: string): string => {
    for (const c of spec.columns) {
      if (c.column !== column) continue;
      const r = c.role;
      if (r.role === 'instance' && r.class) return r.class;
      if (r.role === 'object_property' && r.range_class) return r.range_class;
      if (r.role === 'code' && r.rule.reads === 'term' && r.rule.class) return r.rule.class;
    }
    return '?';
  };
  const instances = spec.columns.filter((c) => c.role.role === 'instance').map((c) => c.column);
  const soleInstance = instances.length === 1 ? instances[0] : undefined;
  return spec.columns.flatMap((c) => {
    const r = c.role;
    const links = r.role === 'instance' || r.role === 'object_property' || r.role === 'code' ? (r.links ?? []) : [];
    const own =
      r.role === 'object_property' && r.predicate.trim() && (r.from_column || soleInstance)
        ? [
            `${classOf(r.from_column ?? soleInstance ?? '')} ${r.predicate} ${classOf(c.column)}${r.context ? ` @context ${classOf(r.context)}` : ''}`,
          ]
        : [];
    return [
      ...own,
      ...links.map(
        (l) =>
          `${classOf(c.column)} ${l.predicate} ${classOf(l.to_column)}${l.context ? ` @context ${classOf(l.context)}` : ''}`,
      ),
    ];
  });
}

/** The cell answers that make sense under each header. A Class column's cell
 *  is its thing's name, its ID, an ID that is also a value, or a code that
 *  names a term of a list; an Attribute is a value, the name of the thing it
 *  describes, a code that asserts something, or the kind of that thing. */
export const CELL_CHOICES: Record<'class' | 'relationship' | 'attribute', CellChoice[]> = {
  class: ['label', 'uuid', 'value', 'qualifier'],
  relationship: ['label', 'uuid'],
  attribute: ['value', 'qualifier', 'class'],
};

/** Whether a header's cells need an answer of their own: an annotation's
 *  cells are that text and nothing else. */
export function cellsAsked(header: HeaderChoice): header is 'class' | 'relationship' | 'attribute' {
  return header === 'class' || header === 'relationship' || header === 'attribute';
}

/** The mapping a header and cell answer make, from the mapping as it stands.
 *  Everything the answers do not decide is kept, so switching while deciding
 *  throws no work away. */
export function applyShape(m: ColumnMapping, header: HeaderChoice, cell: CellChoice | null): ColumnMapping {
  const base: ColumnMapping = { ...m, preservedRole: undefined, relationship: false };
  if (header === 'skip' || header === 'carried') return { ...base, job: 'skip' };
  const notAnnotation = annotationOf(m.predicate) ? '' : m.predicate;
  if (!cellsAsked(header)) {
    // An annotation of some thing: which thing is chosen in step 2, as for
    // any attribute. A Class column's own name is not carried across.
    const on = isThingColumn(m) ? '' : m.onColumn;
    return { ...base, job: 'attribute', predicate: ANNOTATION_PREDICATES[header], onColumn: on, context: '' };
  }
  const allowed = CELL_CHOICES[header];
  const current = cellChoice(m);
  const c: CellChoice =
    cell && allowed.includes(cell)
      ? cell
      : current && allowed.includes(current)
        ? current
        : header === 'attribute'
          ? 'value'
          : 'label';
  if (header === 'class' || header === 'relationship') {
    if (c === 'qualifier') return { ...base, job: 'code', codeReads: 'term', onColumn: '', predicate: '' };
    const wasLabel = current === 'label' && m.job === 'id';
    // A relationship column keeps its statement (subject, name, context); a
    // Class column stands on its own.
    const relationship = header === 'relationship';
    const id = {
      ...base,
      job: 'id' as const,
      relationship,
      onColumn: relationship && !isThingColumn(m) ? '' : relationship ? m.onColumn : '',
      predicate: relationship ? notAnnotation : '',
      context: relationship ? m.context : '',
    };
    // The resolution follows the cells, as the proposal gives it (ruled
    // 2026-09-30): label cells match an entity the store holds by its label
    // or an alternative label, else mint; identifiers as written have no
    // label to match. A choice made under More options stays while the cells
    // stay.
    if (c === 'label') {
      return {
        ...id,
        isLabel: true,
        keyValueType: '',
        idStrategy: wasLabel ? m.idStrategy : 'generated',
        keyMatch: wasLabel ? m.keyMatch : 'match_or_create',
      };
    }
    const fromCell = m.idStrategy === 'generated' ? 'from_column' : m.idStrategy;
    const keyMatch = wasLabel ? 'mint' : m.keyMatch;
    if (c === 'uuid') return { ...id, isLabel: false, keyValueType: '', idStrategy: fromCell, keyMatch };
    return { ...id, isLabel: false, idStrategy: fromCell, keyValueType: m.keyValueType || m.valueType, keyMatch };
  }
  const onColumn = isThingColumn(m) ? '' : m.onColumn;
  switch (c) {
    case 'qualifier':
      return { ...base, job: 'code', codeReads: 'means', onColumn };
    case 'class':
      return { ...base, job: 'kind', onColumn };
    default:
      return { ...base, job: 'attribute', predicate: notAnnotation, onColumn };
  }
}

function valueDatatype(t: ValueTypeChoice | ''): string | null {
  switch (t) {
    case 'number':
      return 'xsd:decimal';
    case 'integer':
      return 'xsd:integer';
    case 'date':
      return 'xsd:date';
    case 'time':
      return 'xsd:time';
    case 'datetime':
      return 'xsd:dateTime';
    case 'boolean':
      return 'xsd:boolean';
    // Text is stored as the text it is: with no datatype the engine reads a
    // jersey number "07" as the number 7.
    case 'text':
      return 'xsd:string';
    default:
      return null;
  }
}

function datatypeToValueType(d: string | null): ValueTypeChoice {
  switch (d) {
    case 'xsd:decimal':
      return 'number';
    case 'xsd:integer':
      return 'integer';
    case 'xsd:date':
      return 'date';
    case 'xsd:time':
      return 'time';
    case 'xsd:dateTime':
      return 'datetime';
    case 'xsd:boolean':
      return 'boolean';
    default:
      return 'text';
  }
}

/** The statements a column adds to the ontology's picture, in its own terms:
 *  `:Player :competesIn :Game`, `:Player :minutes xsd:integer`. What is not
 *  chosen yet reads `?`, so the row shows what is still missing. */
export function ontologyStatements(
  m: ColumnMapping,
  mappings: ColumnMapping[],
  soleStandalone: string | null,
): string[] {
  const classOf = (column: string) =>
    mappings.find((x) => x.column === column && isThingColumn(x))?.klass.trim() || '?';
  const subject = classOf(m.onColumn.trim() || soleStandalone || '');
  const predicate = m.predicate.trim() || mintedPredicate(m.column);
  const own = m.klass.trim() || '?';
  const typed = `?${m.column.replace(/\W+/g, '_')} :type ${own}`;
  // Links other rows declare to this one: the row is their object.
  const incoming = mappings.flatMap((x) =>
    isThingColumn(x) && x.column !== m.column
      ? x.links.filter((l) => l.to === m.column).map((l) => `${classOf(x.column)} ${l.predicate} ${own}`)
      : [],
  );
  const outgoing = m.links.map((l) => `${own} ${l.predicate} ${classOf(l.to)}`);
  switch (m.job) {
    case 'id':
      if (m.keyValueType)
        return [typed, `${own} ${predicate} ${valueDatatype(m.keyValueType) ?? 'xsd:string'}`, ...incoming, ...outgoing];
      return [typed, ...incoming, ...outgoing];
    case 'attribute':
      return [`${subject} ${predicate} ${predicate === ':label' ? 'xsd:string' : (valueDatatype(m.valueType) ?? 'xsd:string')}`];
    case 'code':
      return m.codeReads === 'term' ? [typed, ...incoming, ...outgoing] : [];
    case 'kind':
      return [`${subject} :type (the class each cell names)`];
    default:
      return [];
  }
}

/** The statement one relationship makes, in the ontology's terms. */
export function linkStatement(mappings: ColumnMapping[], from: string, link: MappingLink): string {
  const classOf = (column: string) =>
    mappings.find((x) => x.column === column && isThingColumn(x))?.klass.trim() || '?';
  return `${classOf(from)} ${link.predicate.trim() || '?'} ${classOf(link.to)}`;
}

/** A class name a header suggests: `game_id` holds Games, `home_team`
 *  Home Teams. Only a suggestion for the create dialog; the user names it. */
export function suggestedClassName(header: string): string {
  const words = header
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .split(/[^A-Za-z0-9]+/)
    .filter((w) => w && !/^(id|uuid|key|code|no|num|number)$/i.test(w));
  return words.map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join('');
}

/** The property a blank field takes from the column's header: the header's
 *  words, lower-cased and joined by underscores. The same rule as the
 *  server's `predicate_for_header`, so a suggestion and a blank field name
 *  one property, not two. */
export function mintedPredicate(column: string): string {
  const words = column.split(/[^\p{L}\p{N}]+/u).filter(Boolean);
  return words.length === 0 ? ':field' : `:${words.join('_').toLowerCase()}`;
}

/** A property or class as the person typed it: a term as written, or one
 *  made from plain words with their spelling kept (`has_Referee`). */
export function termFromWords(text: string): string {
  const t = text.trim();
  if (!t) return '';
  return t.includes(':') ? t : `:${t.replace(/[^\p{L}\p{N}]+/gu, '_')}`;
}

/** QROS CORE-language predicates — always available, part of the spec, never
 *  minted by an import. A term here is "core", not "new": the ontology need
 *  not (and usually does not) redeclare it. */
const CORE_PREDICATES = new Set<string>([
  ':label',
  ':prefLabel',
  ':altLabel',
  ':comment',
  ':definition',
  ':scopeNote',
  ':note',
  ':example',
  ':type',
  ':subClassOf',
  ':subPropertyOf',
  ':domain',
  ':range',
]);

/** True when the predicate is part of the QROS core language rather than a
 *  dataset-declared or user-minted term. */
export function isCorePredicate(term: string): boolean {
  return CORE_PREDICATES.has(term.trim());
}

/** The declared property the field's text names: exact term, else a unique
 *  case-insensitive label match. Ambiguity resolves to nothing — the field
 *  then reads as a new term and the user picks explicitly. */
export function matchedProperty(schema: OntologySchema | null | undefined, text: string): SchemaProperty | null {
  const t = text.trim();
  if (!schema || !t) return null;
  const byUri = schema.properties.find((p) => p.uri === t);
  if (byUri) return byUri;
  const byLabel = schema.properties.filter((p) => p.label?.toLowerCase() === t.toLowerCase());
  return byLabel.length === 1 ? (byLabel[0] ?? null) : null;
}

/** The declared class the field's text names (same rules as matchedProperty). */
export function matchedClass(schema: OntologySchema | null | undefined, text: string): SchemaClass | null {
  const t = text.trim();
  if (!schema || !t) return null;
  const byUri = schema.classes.find((c) => c.uri === t);
  if (byUri) return byUri;
  const byLabel = schema.classes.filter((c) => c.label?.toLowerCase() === t.toLowerCase());
  return byLabel.length === 1 ? (byLabel[0] ?? null) : null;
}

/** A declared property's shape, compactly: "Model → Vendor". */
export function propertyShape(p: SchemaProperty): string {
  if (p.domain.length === 0 && p.range.length === 0) return '';
  return `${p.domain.join(', ') || '…'} → ${p.range.join(', ') || '…'}`;
}

/** The rule rows as the engine's `CodeMeaning[]`: one entry per value, with
 *  every statement that value makes. Blank rows are dropped rather than
 *  emitted as a rule asserting nothing. */
function meaningsToRule(rows: CodeMeaningRow[]): CodeMeaning[] {
  const byValue = new Map<string, CodeMeaning>();
  for (const r of rows) {
    const value = r.value.trim();
    const predicate = r.predicate.trim();
    const object = r.object.trim();
    if (!value || !predicate || !object) continue;
    const existing = byValue.get(value);
    const assertion = { predicate, object, object_is_iri: r.objectIsIri };
    if (existing) existing.asserts.push(assertion);
    else byValue.set(value, { value, asserts: [assertion] });
  }
  return [...byValue.values()];
}

function ruleToMeanings(values: CodeMeaning[]): CodeMeaningRow[] {
  return values.flatMap((v) =>
    v.asserts.map((a) => ({
      value: v.value,
      predicate: a.predicate,
      object: a.object,
      objectIsIri: a.object_is_iri,
    })),
  );
}

/** A row's links as the engine's, each holding in the row's context. */
function linksOf(m: ColumnMapping) {
  return m.links
    .filter((l) => l.predicate.trim() && l.to.trim())
    .map((l) => ({
      predicate: l.predicate.trim(),
      to_column: l.to.trim(),
      ...(l.context.trim() ? { context: l.context.trim() } : {}),
    }));
}

/** Lower one grid row to the engine's ColumnRole. */
/** `alone`: an Entity column whose property row is not in play
 *  (`carriesPropertyRow`); it lowers as its own thing. */
function mappingToRole(m: ColumnMapping, alone = false): ColumnRole {
  const predicate = m.predicate.trim() || mintedPredicate(m.column);
  const onColumn = m.onColumn.trim();
  // Emitted only when set, so a spec without contexts stays byte for byte.
  const context = m.context.trim() ? { context: m.context.trim() } : {};
  switch (m.job) {
    case 'skip':
      return { role: 'skip' };
    case 'tag':
      // Unreachable: a carried tag returns through `preservedRole` before this
      // is called. Rebuilding one from a grid row that never represented it
      // would be inventing a role.
      return { role: 'skip' };
    case 'id': {
      // Repeated values are one thing either way; matching links that thing
      // to an existing entity of the same name (the id strategy still decides
      // the IRI minted when there is no match).
      const resolution = keyMatchToResolution(m.keyMatch);
      if (m.relationship && !alone) {
        // The header names a relationship: the engine's reference role, the
        // statement's subject named by the column it comes from.
        const links = linksOf(m);
        return {
          role: 'object_property',
          predicate,
          range_class: m.klass || null,
          resolution,
          required: false,
          generated_id: m.idStrategy === 'generated',
          ...(onColumn ? { from_column: onColumn } : {}),
          ...context,
          ...(links.length ? { links } : {}),
        };
      }
      // Every Class column is its own thing; how it relates to others is its
      // links (ruled 2026-09-28: a column does not define a relationship).
      const links = linksOf(m);
      return {
        role: 'instance',
        class: m.klass || null,
        is_label: m.isLabel,
        fill_down: m.carryDown,
        // Emitted only when set, so a spec without links stays byte for byte.
        ...(links.length ? { links } : {}),
        resolution,
        id_strategy:
          m.idStrategy === 'composite'
            ? { kind: 'composite', also: m.idAlso }
            : m.idStrategy === 'surrogate'
              ? { kind: 'surrogate' }
              : m.idStrategy === 'generated'
                ? { kind: 'generated' }
                : { kind: 'from_column' },
      };
    }
    case 'kind':
      return { role: 'record_kind', ...(onColumn ? { from_column: onColumn } : {}) };
    case 'attribute': {
      // `:label` and `:comment` have dedicated roles in the engine; using them
      // keeps a server proposal's spec byte for byte what it was.
      const on = onColumn ? { from_column: onColumn } : {};
      if (predicate === ':label') return { role: 'label', ...on };
      if (predicate === ':comment') return { role: 'comment', ...on };
      return {
        role: 'datatype_property',
        predicate,
        datatype: valueDatatype(m.valueType),
        required: false,
        // Emitted only when the value belongs to another column's record. The
        // field is `#[serde(default)]` server-side, so absent and null mean the
        // same thing — and absent keeps an ordinary value column's spec byte
        // for byte what it was, which is what the round-trip tests pin.
        ...(onColumn ? { from_column: onColumn } : {}),
        ...context,
        ...(m.valueType === 'datetime' && m.dateFrom.trim() ? { date_column: m.dateFrom.trim() } : {}),
      };
    }
    case 'code':
      if (m.codeReads === 'term') {
        // A code column is a Class column: its terms are things, related to
        // others through links, and it names no property of its own.
        const links = linksOf(m);
        return {
          role: 'code',
          rule: { reads: 'term', predicate: '', class: m.klass.trim(), create_if_missing: m.createIfMissing },
          ...(links.length ? { links } : {}),
        };
      }
      return {
        role: 'code',
        ...(onColumn ? { from_column: onColumn } : {}),
        ...context,
        rule: { reads: 'means', values: meaningsToRule(m.codeMeanings) },
      };
  }
}

/** Recover grid state from a spec (so the server's inferred defaults land in
 *  the grid, and reopening the step shows what will actually run). */
/** The classes created while mapping, once each, for the spec. */
export function mappingsToNewClasses(mappings: ColumnMapping[]): NewClass[] {
  const out = new Map<string, NewClass>();
  for (const m of mappings) {
    const cls = m.klass.trim();
    if (cls && m.klassParent.trim() && !out.has(cls)) {
      out.set(cls, { class: cls, label: m.klassLabel.trim() || cls.replace(/^:/, ''), parent: m.klassParent.trim() });
    }
  }
  return [...out.values()];
}

export function specToMappings(spec: ImportSpec): ColumnMapping[] {
  // A reference saved before ruling 4.8 relates from "the row's subject" with
  // no column named; that subject was the one instance column, so name it.
  const instances = spec.columns.filter((c) => c.role.role === 'instance').map((c) => c.column);
  const soleInstance = instances.length === 1 ? (instances[0] ?? '') : '';
  const toLinks = (links: { predicate: string; to_column: string; context?: string | null }[] | undefined) =>
    (links ?? []).map((l) => ({ predicate: l.predicate, to: l.to_column, context: l.context ?? '' }));
  // A reference or a code saved with a relation of its own (before 2026-09-28)
  // becomes a Class column, and its relation a link on the column it came
  // from, so reopening a saved mapping shows the two-step picture.
  const inbound: { from: string; link: MappingLink }[] = [];
  const rows: ColumnMapping[] = spec.columns.map((c) => {
    const blankOp = c.normalize.find((n) => n.op === 'blank_if');
    const base: ColumnMapping = {
      ...emptyMapping(c.column),
      carryDown: Boolean(c.fill_down),
      blankTokens: blankOp && blankOp.op === 'blank_if' ? blankOp.tokens : [],
      replacePairs: c.normalize.flatMap((n) => (n.op === 'replace' ? [{ from: n.from, to: n.to }] : [])),
      regexPattern: c.normalize.find((n) => n.op === 'regex_replace')?.pattern ?? '',
      regexReplacement: c.normalize.find((n) => n.op === 'regex_replace')?.replacement ?? '',
    };
    const r = c.role;
    switch (r.role) {
      case 'skip':
        return base;
      case 'instance':
        return {
          ...base,
          job: 'id',
          keyMatch: resolutionToKeyMatch(r.resolution),
          klass: r.class ?? '',
          carryDown: r.fill_down,
          isLabel: r.is_label,
          idStrategy: r.id_strategy.kind,
          idAlso: r.id_strategy.kind === 'composite' ? r.id_strategy.also : [],
          links: toLinks(r.links),
        };
      case 'label':
        return { ...base, job: 'attribute', predicate: ':label', onColumn: r.from_column ?? '' };
      case 'comment':
        return { ...base, job: 'attribute', predicate: ':comment', onColumn: r.from_column ?? '' };
      case 'record_kind':
        return { ...base, job: 'kind', onColumn: r.from_column ?? '' };
      case 'object_property':
        // The header names a relationship (4.9). A reference saved before
        // ruling 4.8 relates from "the row's subject" with no column named;
        // that subject was the one instance column.
        return {
          ...base,
          job: 'id',
          relationship: true,
          onColumn: r.from_column ?? soleInstance,
          predicate: r.predicate,
          context: r.context ?? '',
          keyMatch: resolutionToKeyMatch(r.resolution),
          idStrategy: r.generated_id ? 'generated' : 'from_column',
          isLabel: true,
          klass: r.range_class ?? '',
          links: toLinks(r.links),
        };
      case 'datatype_property':
        return {
          ...base,
          job: 'attribute',
          predicate: r.predicate,
          valueType: datatypeToValueType(r.datatype),
          onColumn: r.from_column ?? '',
          context: r.context ?? '',
          dateFrom: r.date_column ?? '',
        };
      case 'code': {
        if (r.rule.reads === 'term') {
          const from = r.from_column ?? soleInstance;
          if (from && r.rule.predicate.trim()) {
            inbound.push({ from, link: { predicate: r.rule.predicate, to: c.column, context: r.context ?? '' } });
          }
          return {
            ...base,
            job: 'code',
            codeReads: 'term',
            klass: r.rule.class,
            createIfMissing: r.rule.create_if_missing,
            links: toLinks(r.links),
          };
        }
        return {
          ...base,
          job: 'code',
          onColumn: r.from_column ?? '',
          context: r.context ?? '',
          codeReads: 'means',
          codeMeanings: ruleToMeanings(r.rule.values),
        };
      }
      case 'tag':
        // The grid cannot author a tag column, so it carries the role verbatim
        // and hands it back untouched. Surfacing it as Skip — which is what
        // this did — meant the next edit to ANY column rewrote it as skip for
        // real, and the tag was gone with no warning.
        return { ...base, job: 'tag', preservedRole: r };
    }
  });
  for (const { from, link } of inbound) {
    const source = rows.find((m) => m.column === from && isThingColumn(m));
    if (source) source.links = [...source.links, link];
  }
  // A class created in an earlier session comes back with its parent.
  const created = new Map((spec.new_classes ?? []).map((c) => [c.class, c]));
  for (const m of rows) {
    const c = created.get(m.klass.trim());
    if (c) {
      m.klassParent = c.parent;
      m.klassLabel = c.label;
    }
  }
  return foldKeyValueColumns(rows);
}

/** A key that also records a value is TWO spec columns on one source column —
 *  the instance that mints the subject and the datatype property that writes
 *  the fact. The grid shows one row per source column, so fold the second back
 *  onto the first and drop it. Without this, reopening the step would show the
 *  key twice and the second row would look like an unrelated value column. */
function foldKeyValueColumns(rows: ColumnMapping[]): ColumnMapping[] {
  const keyed = new Set(rows.filter((m) => m.job === 'id' && !m.onColumn).map((m) => m.column));
  const out: ColumnMapping[] = [];
  for (const m of rows) {
    const isKeysValue =
      m.job === 'attribute' && (!m.onColumn || m.onColumn === m.column) && keyed.has(m.column);
    if (isKeysValue) {
      const key = out.find((k) => k.column === m.column && k.job === 'id');
      if (key) {
        key.keyValueType = m.valueType;
        key.predicate = m.predicate;
        continue;
      }
    }
    out.push(m);
  }
  return out;
}

export function mappingsToColumns(mappings: ColumnMapping[], prev: ColumnSpec[]): ColumnSpec[] {
  // By NAME, not by position. A key column that also records a value folds two
  // spec columns into one grid row, so the two lists are different lengths and
  // an index lookup handed every column after the folded one the PREVIOUS
  // column's normalize ops.
  const before = new Map(prev.map((c) => [c.column, c]));
  return mappings.flatMap((m) => {
    // An unanswered column reads nothing: its suggestion has no effect until
    // it is accepted.
    if (m.unanswered) return [{ column: m.column, role: { role: 'skip' }, normalize: [] } as ColumnSpec];
    // A job the grid cannot author goes back exactly as it came, including its
    // normalize ops: rebuilding it would be inventing a role from a grid row
    // that never represented it.
    if (m.job === 'tag' && m.preservedRole) {
      return [
        {
          column: m.column,
          role: m.preservedRole,
          normalize: before.get(m.column)?.normalize ?? [],
        } as ColumnSpec,
      ];
    }
    // Normalize ops FOLLOW the visible controls (audit W10/W12): replace/blank/
    // boolean/date_iso are all rebuilt from the current mapping so the grid and
    // the stored ops never drift. Anything else the server attached is carried
    // through untouched.
    // A date, a time or a date with a time is read into ISO form whatever the
    // sheet's spelling (61.35); the op follows the chosen kind of value.
    const valueType = m.job === 'attribute' ? m.valueType : m.job === 'id' ? m.keyValueType : '';
    const isoOp =
      valueType === 'date'
        ? 'date_iso'
        : valueType === 'time'
          ? 'time_iso'
          : valueType === 'datetime'
            ? 'date_time_iso'
            : null;
    const isBoolean = m.job === 'attribute' && m.valueType === 'boolean';
    const others = (before.get(m.column)?.normalize ?? []).filter(
      (n) =>
        n.op !== 'replace' &&
        n.op !== 'blank_if' &&
        n.op !== 'to_boolean' &&
        n.op !== 'regex_replace' &&
        !((n.op === 'date_iso' || n.op === 'time_iso' || n.op === 'date_time_iso') && n.op !== isoOp),
    );
    if (isoOp && !others.some((n) => n.op === isoOp)) {
      others.push({ op: isoOp });
    }
    // Replaces run first (the id/value builds before parsing). Kept for the key
    // case as before, and also emitted for attributes so the "Alter" editor
    // round-trips losslessly.
    // ID columns included: reformatting a sheet's own identifier for use as
    // an ID (123.456.789 → 123-456-789) is the same replace (61.32).
    const replaces: Normalize[] =
      m.job === 'id' || m.job === 'attribute'
        ? m.replacePairs.map((p) => ({ op: 'replace', from: p.from, to: p.to }))
        : [];
    if (m.job === 'id' && m.regexPattern.trim()) {
      replaces.push({ op: 'regex_replace', pattern: m.regexPattern, replacement: m.regexReplacement });
    }
    const cleanBlank = m.blankTokens.map((t) => t.trim()).filter(Boolean);
    const leading: Normalize[] = cleanBlank.length ? [{ op: 'blank_if', tokens: cleanBlank }] : [];
    const trailing: Normalize[] = isBoolean ? [{ op: 'to_boolean' }] : [];
    const role = mappingToRole(m, !carriesPropertyRow(m, mappings));
    const spec: ColumnSpec = {
      column: m.column,
      role,
      normalize: [...leading, ...replaces, ...others, ...trailing],
      // An ID standing alone carries its key down through its own role;
      // every other column says so here. Emitted only when set, so a spec
      // without it stays byte for byte.
      ...(m.carryDown && role.role !== 'instance' ? { fill_down: true } : {}),
    };
    // A key that also records a value: a second spec column on the SAME source
    // column, writing the typed fact. Safe because the lowering finds its
    // subject by filtering for the instance role and collects properties per
    // column rather than keying by it — so one column doing two jobs is two
    // entries, not a conflict.
    if (m.job === 'id' && !m.onColumn && m.keyValueType) {
      return [
        spec,
        {
          column: m.column,
          role: {
            role: 'datatype_property',
            predicate: m.predicate || mintedPredicate(m.column),
            datatype: valueDatatype(m.keyValueType),
            required: false,
            // The fact is about this ID column's own entity. Naming it keeps
            // the spec valid when the sheet has several ID columns (4.8).
            from_column: m.column,
          },
          normalize: [...leading, ...replaces, ...others],
        } as ColumnSpec,
      ];
    }
    return [spec];
  });
}

// ---------------------------------------------------------------------------
// The three pages (ruled 2026-09-30, decision 4.10): what each column IS, the
// properties, the rules. Layered over the job model above, which the spec
// lowering keeps.
// ---------------------------------------------------------------------------

/** What a column is, page 1. */
export type ColumnKind = 'entity' | 'datatype' | 'annotation' | 'rule' | 'skip' | 'carried';

/** What a column is, in the words of the person who made the sheet (ruled
 *  2026-09-30: the RDF terms are the base, with a guidance layer over them).
 *  The term each answer stands for is `KIND_TERMS`, shown beside it. */
export const KIND_WORDS: Record<ColumnKind, string> = {
  entity: 'A thing in a list',
  datatype: 'A fact about a thing',
  annotation: 'A note or name',
  rule: 'A code that stands for a fact',
  skip: 'Skip',
  carried: 'Tag (saved mapping)',
};

/** The ontology term each page-1 answer stands for. */
export const KIND_TERMS: Record<ColumnKind, string | null> = {
  entity: 'Entity',
  datatype: 'DatatypeProperty',
  annotation: 'AnnotationProperty',
  rule: 'Rule',
  skip: null,
  carried: null,
};

/** The kinds of value a fact can hold, in plain words, each with its
 *  datatype. */
export const DATATYPE_CHOICES: { value: ValueTypeChoice; words: string }[] = [
  { value: 'text', words: 'Text' },
  { value: 'integer', words: 'Whole number' },
  { value: 'number', words: 'Number' },
  { value: 'boolean', words: 'Yes or no' },
  { value: 'date', words: 'Date' },
  { value: 'time', words: 'Time of day' },
  { value: 'datetime', words: 'Date and time' },
];

/** A kind of value in plain words. */
export function datatypeWords(t: ValueTypeChoice): string {
  return DATATYPE_CHOICES.find((d) => d.value === t)?.words ?? 'Text';
}

/** The annotation properties QROS supports, the only ones a mapping may use. */
export const ANNOTATION_PROPERTIES: { predicate: string; label: string }[] = CORE_ANNOTATION_PREDICATES.map((p) => ({
  predicate: p.uri,
  label: p.label,
}));

export function isAnnotationPredicate(predicate: string): boolean {
  return ANNOTATION_PROPERTIES.some((a) => a.predicate === predicate.trim());
}

export function columnKind(m: ColumnMapping): ColumnKind {
  switch (m.job) {
    case 'skip':
      return 'skip';
    case 'tag':
      return 'carried';
    case 'id':
      return 'entity';
    case 'code':
      return m.codeReads === 'term' ? 'entity' : 'rule';
    case 'kind':
      return 'rule';
    case 'attribute':
      return isAnnotationPredicate(m.predicate) ? 'annotation' : 'datatype';
  }
}

/** The mapping a page-1 answer makes. An Entity keeps its class and its cell
 *  answer, and carries a property row of its own for page 2 (the header names
 *  the relationship the column stands in, `relationship`), named from the
 *  header. */
export function applyKind(m: ColumnMapping, kind: ColumnKind): ColumnMapping {
  switch (kind) {
    case 'skip':
    case 'carried':
      return applyShape(m, 'skip', null);
    case 'entity': {
      const cell = cellChoice(m) === 'uuid' ? 'uuid' : 'label';
      const wasEntity = m.job === 'id';
      const e = applyShape(m, 'relationship', cell);
      // A cell that names an entity the store already holds, by its label or
      // an alternative label, is that entity; the rest are minted (ruled
      // 2026-09-30: the first import mints RB, the taxonomy editor makes it
      // Right Back with RB as an alternative label, the next import matches).
      return {
        ...e,
        relationship: wasEntity ? m.relationship : true,
        predicate: wasEntity ? m.predicate : mintedPredicate(m.column),
        keyMatch: wasEntity ? m.keyMatch : 'match_or_create',
      };
    }
    case 'datatype':
      return applyShape(m, 'attribute', 'value');
    case 'annotation':
      return applyShape(m, 'label', null);
    case 'rule':
      return applyShape(m, 'attribute', 'qualifier');
  }
}

/** One row of page 2's ObjectProperties table: the row an Entity column
 *  carries (its object is the column), or a line drawn on its domain column. */
export type ObjectPropertyRow =
  | { kind: 'column'; column: string }
  | { kind: 'link'; from: string; index: number };

/** Whether the mapping refers to an Entity column already: it is the domain
 *  or the context of another column's property, or on either end of a drawn
 *  ObjectProperty. */
export function referredTo(column: string, mappings: ColumnMapping[]): boolean {
  return mappings.some(
    (x) =>
      (x.column === column && isThingColumn(x) && x.links.length > 0) ||
      (x.column !== column && (x.onColumn.trim() === column || x.context.trim() === column)) ||
      x.links.some((l) => l.to === column),
  );
}

/** Whether an Entity column's own property row is in play: only where
 *  another Entity column could be its domain, and only while the mapping
 *  does not account for the column already. The only Entity column of a
 *  sheet stands alone, whatever its flag says; a column that is the subject
 *  or the context of attributes, or on either end of a drawn ObjectProperty,
 *  gets no question (61.69) — a row the person completed stays. */
export function carriesPropertyRow(m: ColumnMapping, mappings: ColumnMapping[]): boolean {
  if (m.job !== 'id' || !m.relationship || mappings.filter(isThingColumn).length <= 1) return false;
  return Boolean(m.onColumn.trim()) || !referredTo(m.column, mappings);
}

export function objectPropertyRows(mappings: ColumnMapping[]): ObjectPropertyRow[] {
  const own = mappings
    .filter((m) => carriesPropertyRow(m, mappings))
    .map((m): ObjectPropertyRow => ({ kind: 'column', column: m.column }));
  const drawn = mappings.flatMap((m) =>
    isThingColumn(m) ? m.links.map((_, index): ObjectPropertyRow => ({ kind: 'link', from: m.column, index })) : [],
  );
  return [...own, ...drawn];
}

/** The class of an Entity column, for a declaration or a statement. */
export function classOfColumn(mappings: ColumnMapping[], column: string): string {
  return mappings.find((x) => x.column === column && isThingColumn(x))?.klass.trim() || '?';
}

/** A datatype as the ontology writes it. */
export function datatypeTerm(t: ValueTypeChoice): string {
  return valueDatatype(t) ?? 'xsd:string';
}

/** The folder report's "Map with a context tag" (81.22): a sheet whose rows
 *  repeat per thing is suggested with that thing's column as an Entity, the
 *  column that tells the repeats apart as an Entity too, and every value
 *  column as a fact about the thing that holds in the other, so each reading
 *  stays a fact of its own. A column suggested with no class gets one created
 *  from its header. Only suggestions change; nothing applies until accepted. */
export function withContextSuggestion(mappings: ColumnMapping[], subject: string, context: string): ColumnMapping[] {
  const asEntity = (column: string): ColumnMapping | null => {
    const m = mappings.find((x) => x.column === column);
    if (!m) return null;
    const base = m.suggestion ?? m;
    if (isThingColumn(base) && base.klass.trim()) return base;
    const e = applyKind(base, 'entity');
    const name = suggestedClassName(column);
    return name ? { ...e, klass: `:${name}`, klassParent: ':Concept', klassLabel: name } : e;
  };
  const thing = asEntity(subject);
  const holder = asEntity(context);
  if (!thing || !holder || subject === context) return mappings;
  return mappings.map((m) => {
    if (!m.unanswered) return m;
    if (m.column === subject) return { ...m, suggestion: thing };
    if (m.column === context) return { ...m, suggestion: holder };
    const s = m.suggestion;
    if (!s || s.job !== 'attribute' || columnKind(s) !== 'datatype') return m;
    return { ...m, suggestion: { ...s, onColumn: s.onColumn.trim() || subject, context } };
  });
}
